import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image, Font } from '@react-pdf/renderer';
import { getBrandingForEmployee } from '../config/brandingStore';
import { formatDate } from '../utils/dateUtils';

Font.register({
  family: 'Amiri',
  src: require("../assets/Amiri.ttf")
});

const MissionOrderPDF = ({
  data
}) => {
  // Obtenir le branding selon le site de l'employé
  const BRAND = getBrandingForEmployee(data.employee);
  
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.container}>
          <View style={styles.logo}>
            <Image src={BRAND.pdfLogo} style={styles.logoImage} />
          </View>

          <Text style={styles.headerDate}>{BRAND.pdfHeader.city} {BRAND.pdfHeader.dateLabel} {formatDate(data.createdAt)}</Text>

          <Text style={styles.mainTitleArabic}>
            أمر مهمة
          </Text>
          <br></br>
          <Text style={styles.mainTitle}>
            ORDRE DE MISSION N° {data.order_number ? (() => {
            const refNumber = data.order_number;
            // Si la référence contient déjà un slash, l'utiliser telle quelle
            if (refNumber.includes('/')) {
              return refNumber;
            }
            // Sinon, formater comme 00XX/YYYY
            const currentYear = new Date(data.createdAt || Date.now()).getFullYear();
            return `${String(refNumber).padStart(4, '0')}/${currentYear}`;
          })() : '........'}
          </Text>

          <View style={styles.formGroup}>
            <Text style={styles.fieldArticle}>
              Suivant article N°33 du règlement intérieur, nous vous ordonnons d'effectuer cette mission.
            </Text>

            {/* NOM */}
            <View style={styles.fieldRow}>
              <Text style={styles.field}>
                NOM : <Text style={styles.fieldValue}>{data.employee?.last_name || '............................'}</Text>
              </Text>
              <Text style={styles.arabicLabel}>اللقب</Text>
            </View>

            {/* PRENOM */}
            <View style={styles.fieldRow}>
              <Text style={styles.field}>
                PRENOM : <Text style={styles.fieldValue}>{data.employee?.first_name || '............................'}</Text>
              </Text>
              <Text style={styles.arabicLabel}>الاسم</Text>
            </View>

            {/* FONCTION */}
            <View style={styles.fieldRow}>
              <Text style={styles.field}>
                FONCTION : <Text style={styles.fieldValue}>{data.employee?.poste || '............................'}</Text>
              </Text>
              <Text style={styles.arabicLabel}>الخدمة</Text>
            </View>

            {/* MOTIF */}
            <View style={styles.fieldRow}>
              <View style={styles.fieldContainer}>
                <Text style={styles.field}>
                  MOTIF : <Text style={styles.fieldValue}>{data.reason || '............................'}</Text>
                </Text>
              </View>
              <Text style={styles.arabicLabel}>الموضوع</Text>
            </View>

            {/* DESTINATIONS */}
            <View style={styles.fieldRow}>
              <View style={styles.fieldContainer}>
                <Text style={styles.field}>
                  DESTINATION(S) : <Text style={styles.fieldValue}>
                    {(() => {
                      // Vérifications de sécurité pour les destinations
                      if (!data.destinations || !Array.isArray(data.destinations) || data.destinations.length === 0) {
                        return '............................';
                      }
                      
                      try {
                        // Récupérer les infos de départ
                        const departureWilaya = data.departure_wilaya?.name || '';
                        const departureDaira = data.departure_daira || '';
                        const departureInfo = departureWilaya && departureDaira ? `${departureWilaya}-${departureDaira}` : departureWilaya || departureDaira || '';
                        
                        // Format pour toutes les destinations
                        const destinationsFormatted = data.destinations.map(dest => {
                          if (!dest) return '';
                          
                          // Format pour l'Algérie
                          if (dest.pays_id === 4) {
                            const wilayaName = dest.wilaya?.name || '';
                            const dairaName = dest.daira || '';
                            return wilayaName && dairaName ? `${wilayaName}-${dairaName}` : wilayaName || dairaName || '';
                          } 
                          // Format pour les autres pays
                          else {
                            const paysName = dest.pays?.name || '';
                            const villeName = dest.ville || '';
                            return paysName && villeName ? `${paysName}-${villeName}` : paysName || villeName || '';
                          }
                        }).filter(dest => dest !== '').join(', ');
                        
                        // Format final: départ , destinations , départ
                        return destinationsFormatted ? `${departureInfo} , ${destinationsFormatted} , ${departureInfo}` : departureInfo || '............................';
                      } catch (error) {
                        console.error('❌ Erreur formatage destinations:', error);
                        return '............................';
                      }
                    })()}
                  </Text>
                </Text>
              </View>
              <Text style={styles.arabicLabel}>الوجهة</Text>
            </View>

            {/* MOYEN DE TRANSPORT */}
            <View style={styles.fieldRow}>
              <View style={styles.fieldContainer}>
                <Text style={styles.field}>
                  MOYEN DE TRANSPORT : <Text style={styles.fieldValue}>
                    {data.transport?.type || '............................'}{data.transport?.brand && data.transport?.model ? ` - ${data.transport.brand} ${data.transport.model}` : ''}
                  </Text>
                </Text>
              </View>
              <Text style={styles.arabicLabel}>وسيلة النقل</Text>
            </View>

            {/* BILLET (si avion) */}
            {data.transport?.type?.toLowerCase() === 'avion' && (
              <View style={styles.fieldRow}>
                <View style={styles.fieldContainer}>
                  <Text style={styles.field}>
                    BILLET : <Text style={styles.fieldValue}>
                      {data.billet || '............................'}
                    </Text>
                  </Text>
                </View>
                <Text style={styles.arabicLabel}>تذكرة الطيران</Text>
              </View>
            )}

            {/* DATE DE DEBUT */}
            <View style={styles.fieldRow}>
              <Text style={styles.field}>
                DATE ET HEURE DE DEBUT : <Text style={styles.fieldValue}>{formatDate(data.validity_from) || '............................'} .................</Text>
              </Text>
              <Text style={styles.arabicLabel}>تاريخ البدء</Text>
            </View>

            {/* DATE DE FIN */}
            <View style={styles.fieldRow}>
              <Text style={styles.field}>
                DATE ET HEURE DE FIN : <Text style={styles.fieldValue}>{formatDate(data.validity_to) || '............................'} .................</Text>
              </Text>
              <Text style={styles.arabicLabel}>تاريخ الانتهاء</Text>
            </View>
          </View>

          <View style={styles.spacer} />

          <Text style={styles.fieldNote}>
            N.B : <Text style={styles.fieldValueFooter}>le présent ordre doit être remis à la direction.             </Text>
            <Text style={styles.fieldArabic}>:ملاحظة</Text>
            <Text style={styles.fieldValueArabic}>يجب ارجاع أمر المهمة الى الإدارة</Text>
          </Text>

          <View style={styles.spacerSmall} />

          <View style={styles.footer}>
            <View style={styles.spacer} />
            <Text style={styles.signature}>Le Directeur                                                                 L'employé(e)</Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

const styles = StyleSheet.create({
  page: {
    padding: 40,
    fontFamily: 'Helvetica',
    fontSize: 11,
    lineHeight: 1.5,
    display: 'flex',
    flexDirection: 'column',
  },
  container: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  logo: {
    width: '100%',
    marginBottom: 10,
  },
  logoImage: {
    width: '100%',
  },
  headerDate: {
    fontSize: 11,
    textAlign: 'right',
    marginBottom: 18,
  },
  arabicTitle: {
    fontFamily: 'Helvetica',
    fontSize: 16,
    textAlign: 'right',
    textDecoration: 'underline',
    marginBottom: 10,
  },
  mainTitleArabic: {
    fontFamily: 'Amiri',
    fontSize: 22,
    textAlign: 'center',
    textDecoration: 'underline',
    fontWeight: 'bold',
    marginBottom: 13,
  },
  mainTitle: {
    marginTop: 10,
    fontSize: 16,
    textAlign: 'center',
    textDecoration: 'underline',
    fontWeight: 'bold',
    marginBottom: 20,
  },
  formGroup: {
    paddingLeft: 30,
    marginBottom: 10,
  },
  fieldArabic: {
    fontSize: 14,
    fontFamily: 'Amiri',
    textAlign: 'right',
  },
  fieldValue: {
    fontSize: 15,
    fontFamily: 'Amiri',
    fontWeight: 'normal',
  },
  fieldValueFooter: {
    fontFamily: 'Amiri',
    fontWeight: 'normal',
  },
  fieldValueArabic: {
    fontFamily: 'Amiri',
    fontWeight: 'normal',
  },
  spacer: {
    flexGrow: 1,
  },
  footer: {
    marginTop: 20,
    paddingLeft: 30,
  },
  signature: {
    fontSize: 13,
    fontWeight: 'bold',
    marginBottom: 80,
  },
  fieldNote: {
    fontSize: 12,
    marginBottom: 20,
    paddingLeft: 30,
    paddingRight: 30,
  },
  fieldContainer: {
    flex: 1,
    marginRight: 10,
  },
  arabicLabel: {
    fontFamily: 'Amiri',
    fontSize: 15,
    marginTop: 0,
    alignSelf: 'flex-start',
  },
  fieldRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: 9,
  },
  field: {
    fontWeight: 'bold',
    fontSize: 12,
  },
  spacerSmall: {
    flexGrow: 0.5,
  },
});

export default MissionOrderPDF;
